import {useState} from "react";
import AnnualReportForm from "../../../model/AnnualReportForm";
import PublicCodeRepository from "../../../model/PublicCodeRepository";

const PUBLIC_CODE_USAGE_QUESTION_TEXT = `Do you use any publicly accessible online repositories to store/share code and 
            resources associated with your work on UK Biobank data and this Research Project? If so, please provide 
            details on any other research output (e.g. websites, patents3, GWAS summary statistics location).`;
const PUBLIC_CODE_USAGE_GUIDELINES_URL = `https://www.ukbiobank.ac.uk/media/1htpo5kf/policy-for-usage-of-online-code-repositories_v1_final.pdf`;
const MTA_CLARIFICATION = `* As stated in the Material Transfer Agreement, the Applicant PI must provide UK Biobank with 
            a copy of any patents whose claims cover, or are intended to cover, an Applicant Generated Invention within 
            2 months of the patent rights being granted.`;
const REPOSITORY_USAGE_REQUEST = `Please provide the details of the online repositories you use (such as, GitHub)`;

/**
 * Props for this component
 */
interface PublicCodeUsageQuestionProps {
   form: AnnualReportForm,
   isLocked: boolean,
}

/**
 * Collaborator Annual Report question regarding the use of external resources.
 *
 * Used in: {@link CollaboratorAnnualReportForm|Collaborator annual report}
 */
export default function PublicCodeUsageQuestion({form, isLocked}: PublicCodeUsageQuestionProps) {
   // State to control the showing/hiding of the text area
   const [inputBoxShowing, setInputBoxShowing] = useState(form.usePublicCodeRepo);
   const [externalResources, setExternalResources] = useState<PublicCodeRepository[]>(splitResources(form.publicCodeRepos, form.locked));

   /**
    * When a row is updated, it will store the new value in its respective element. If the element being updated
    * was the empty one (The array will always have one), it will create a new element at the end of the array.
    *
    * @param index The index of the External Resource in the array
    * @param field Which field is being edited (Name or Link)
    * @param value The new value of the field
    */
   const updateResources = (index: number, field: 'name' | 'link', value: string): void => {
      // Update entry
      const temp: PublicCodeRepository[] = [...externalResources];
      temp[index][field] = value;

      // If it's the last processor and user starts typing, add a new one
      if (index === temp.length - 1 && value !== '') {
         temp.push({name: '', link: ''});
      }

      form.publicCodeRepos = mergeResources(temp);
      setExternalResources(temp);
   }

   /**
    * Remove a processors from the array
    *
    * @param index The index of the element we wish to delete
    */
   const removeResource = (index: number): void => {
      const temp: PublicCodeRepository[] = [...externalResources];
      temp.splice(index, 1);

      form.publicCodeRepos = mergeResources(temp);
      setExternalResources(temp);
   }

   return (
      <div id="extResourceQ" className="annual-report-question">
         <label id="extResourceQMainText" className="question-text">{PUBLIC_CODE_USAGE_QUESTION_TEXT}</label>
         <span id="extResourceQExtraInfo1" className="extra-info">See our guidelines here: <a
            id="extResourceQExtraInfo1Url"
            href={PUBLIC_CODE_USAGE_GUIDELINES_URL}>{PUBLIC_CODE_USAGE_GUIDELINES_URL}</a></span>
         <span id="extResourceQExtraInfo2" className="extra-info">{MTA_CLARIFICATION}</span>
         {isLocked ? (
            <span className="boolean-answer">{form.usePublicCodeRepo ? 'YES' : 'NO'}</span>
         ) : (
            <>
               {/*Radio Button Area*/}
               <div id="extResourceQRadioArea" className="yes-or-no-radio">
                  <input id="extResourceQRadioYesInput" className="form-check-input" type="radio"
                         name="extResourceQRadioSelection"
                     /*Disable the toggle is the radio is selected*/
                         data-bs-toggle={!inputBoxShowing ? "collapse" : ""}
                         data-bs-target="#extResourceQTextInput"
                         aria-expanded="false"
                         aria-controls="extResourceQTextInput"
                         required
                         onChange={() => {
                            form.usePublicCodeRepo = true;
                            setTimeout(() => {
                               setInputBoxShowing(true);
                            }, 10);
                         }}
                         defaultChecked={(form.annualReportId && form.usePublicCodeRepo != null) ? (form.usePublicCodeRepo) : false}
                  />
                  <label id="extResourceQRadioYesText" className="form-check-label"
                         htmlFor="extResourceQRadioYesInput">
                     Yes
                  </label>
                  <input id="extResourceQRadioNoInput" className="form-check-input" type="radio"
                         name="extResourceQRadioSelection"
                         data-bs-toggle={inputBoxShowing ? "collapse" : ""}
                         data-bs-target="#extResourceQTextInput"
                         aria-expanded="false"
                         aria-controls="extResourceQTextInput"
                         required
                         onChange={() => {
                            form.usePublicCodeRepo = false;
                            setTimeout(() => {
                               setInputBoxShowing(false);
                            }, 300);
                         }}
                         defaultChecked={(form.annualReportId && form.usePublicCodeRepo != null) ? (!form.usePublicCodeRepo) : false}
                  />
                  <label id="extResourceQRadioNoText" className="form-check-label"
                         htmlFor="extResourceQRadioNoInput">
                     No
                  </label>
                  <div id="isOutputSensitiveRadioAreaFeedback" className="invalid-feedback">
                     Please select one option.
                  </div>
               </div>
            </>
         )}
         {/*Text input area*/}
         <div className="accordion accordion-flush" id="extResourceQAccordion">
            <div id="extResourceQTextInput"
                 className={`annual-report-question accordion-collapse collapse ${form.usePublicCodeRepo ? "show" : ""}`}
                 data-bs-parent="#extResourceQAccordion">
            <span id="extResourceQResourceText" className="extra-info">
               {REPOSITORY_USAGE_REQUEST}
            </span>
               {/*external Resources' entries*/}
               {externalResources.map((resource, index) => (
                  <div id={`extResourceQResource_${index}`} key={index}
                       className="multi-entry-question">
                     <div id={`extResourceQEntryArea_${index}`}>
                        <input
                           id={`extResourceQResourceName_${index}`}
                           type="text"
                           className="form-control"
                           placeholder="Name"
                           value={resource.name}
                           disabled={isLocked}
                           onChange={(e) => updateResources(index, 'name', e.target.value)}
                           required={(index !== externalResources.length - 1 && !isLocked || externalResources.length === 1)}
                        />
                        <input
                           id={`extResourceQResourceLink_${index}`}
                           type="text"
                           className="form-control"
                           placeholder="Website Link"
                           value={resource.link}
                           disabled={isLocked}
                           onChange={(e) => updateResources(index, 'link', e.target.value)}
                           required={(index !== externalResources.length - 1 && !isLocked || externalResources.length === 1)}
                        />
                     </div>
                     {/*Show delete button if this is not the empty element*/}
                     {(index !== externalResources.length - 1 && !isLocked) ?
                        (<button id={`extResourceQResourceRemove_${index}`}
                                 type="button"
                                 className="btn-close"
                                 aria-label="Close"
                                 onClick={() => removeResource(index)}></button>
                        ) : null
                     }
                  </div>
               ))}
            </div>
            <div id="extResourceQTextInputHide"
                 className="accordion-collapse collapse"
                 data-bs-parent="#extResourceQAccordion">
            </div>
         </div>
      </div>
   );
}


/**
 * This method will gather the data from the external resources and merge it into a single string
 *
 * @param resources The array of resources
 */
function mergeResources(resources: PublicCodeRepository[]): string {
   let stringWithResources: string = "";

   for (let i = 0; i < resources.length - 1; i++) {
      // Don't include the ';' at the beginning
      if (i === 0) {
         stringWithResources = `${encodeURIComponent(resources[i].name)}|${encodeURIComponent(resources[i].link)}`;
         continue;
      }
      stringWithResources = `${stringWithResources};${encodeURIComponent(resources[i].name)}|${encodeURIComponent(resources[i].link)}`;
   }

   return stringWithResources;
}

/**
 * This function will separate the external resources associated with this report
 *
 * @param formString The resources on the form (Can be null)
 * @param isLocked A boolean to show if the report is blocked or not so we can show the empty row
 */
function splitResources(formString: string | null, isLocked: boolean): PublicCodeRepository[] {
   const arrayOfResources: PublicCodeRepository[] = [];
   if (formString) {
      // Split into individual processors
      const splitString: string[] = formString.split(';');

      splitString.forEach((line: string) => {
         // Split into individual parts
         const parts: string[] = line.split('|');
         arrayOfResources.push({
            name: decodeURIComponent(parts[0]),
            link: decodeURIComponent(parts[1])
         });
      })
   }

   if (!isLocked) {
      arrayOfResources.push({name: '', link: ''})
   }

   return arrayOfResources;
}

